import axios from 'axios';

const API = axios.create({ baseURL: '/api' });

// Attach JWT token automatically to every request
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('gymToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Redirect to login on 401
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('gymToken');
      localStorage.removeItem('gymOwner');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (data) => API.post('/auth/login', data),
  getProfile: () => API.get('/auth/profile'),
  updateProfile: (data) => API.put('/auth/profile', data),
  changePassword: (data) => API.put('/auth/change-password', data),
};

export const dashboardAPI = {
  get: () => API.get('/dashboard'),
  getTrend: () => API.get('/dashboard/trend'),
};

export const membersAPI = {
  getAll: (params) => API.get('/members', { params }),
  getOne: (id) => API.get(`/members/${id}`),
  getExpiring: () => API.get('/members/expiring'),
  create: (data) => API.post('/members', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => API.put(`/members/${id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => API.delete(`/members/${id}`),
  freeze: (id) => API.patch(`/members/${id}/freeze`),
  renew: (id, data) => API.post(`/members/${id}/renew`, data),
};

export const attendanceAPI = {
  mark: (data) => API.post('/attendance/mark', data),
  getToday: (params) => API.get('/attendance/today', { params }),
  getByDate: (date) => API.get(`/attendance/date/${date}`),
  getMembersForAttendance: (params) => API.get('/attendance/members', { params }),
  getMemberAttendance: (memberId, params) => API.get(`/attendance/member/${memberId}`, { params }),
  getStreaks: () => API.get('/attendance/streaks'),
  addWalkIn: (data) => API.post('/attendance/walkin', data),
  getWalkIns: (params) => API.get('/attendance/walkins', { params }),
};

export const paymentsAPI = {
  getAll: (params) => API.get('/payments', { params }),
  getPending: () => API.get('/payments/pending'),
  getDaily: (params) => API.get('/payments/daily', { params }),
  calculate: (memberId) => API.get(`/payments/calculate/${memberId}`),
  record: (data) => API.post('/payments', data),
  quickPay: (data) => API.post('/payments/quick-pay', data),
  update: (id, data) => API.put(`/payments/${id}`, data),
  settle: (id, data) => API.post(`/payments/${id}/settle`, data),
  delete: (id) => API.delete(`/payments/${id}`),
};

export const settingsAPI = {
  getPricing: () => API.get('/settings/pricing'),
  updatePricing: (data) => API.put('/settings/pricing', data),
};

export const supplementsAPI = {
  getAll: () => API.get('/supplements'),
  getLowStock: () => API.get('/supplements/lowstock'),
  getSales: (params) => API.get('/supplements/sales', { params }),
  create: (data) => API.post('/supplements', data),
  update: (id, data) => API.put(`/supplements/${id}`, data),
  delete: (id) => API.delete(`/supplements/${id}`),
  sell: (data) => API.post('/supplements/sell', data),
};

export const expensesAPI = {
  getAll: (params) => API.get('/expenses', { params }),
  getMonthly: (params) => API.get('/expenses/monthly', { params }),
  create: (data) => API.post('/expenses', data),
  update: (id, data) => API.put(`/expenses/${id}`, data),
  delete: (id) => API.delete(`/expenses/${id}`),
};

export const reportsAPI = {
  getMembers: (params) => API.get('/reports/members', { params }),
  getAttendance: (params) => API.get('/reports/attendance', { params }),
  getPayments: (params) => API.get('/reports/payments', { params }),
  getSupplements: (params) => API.get('/reports/supplements', { params }),
  getExpenses: (params) => API.get('/reports/expenses', { params }),
  getProfitLoss: (params) => API.get('/reports/profit-loss', { params }),
};

export default API;
